// ─────────────────────────────────────────────────────────────────────────────
// RF-008 — Selector de avatar Frontline (DINÁMICO).
//
// El catálogo de avatares ya NO es una lista fija de colores dibujados a mano: el backend lo expone
// en GET v1/users/avatars (anónimo) como blobs con URL SAS lista para `<img>`. El picker pinta ese
// catálogo (1..N imágenes) con estados reales (cargando/vacío/error), persiste el ID elegido vía
// PATCH v1/users/{id}/avatar (useUpdateAvatar, body { avatar: id }) y marca el seleccionado.
// El avatar de un usuario se resuelve id→url con `useAvatarUrl` (catálogo cacheado) — ver UserAvatar.
// Sin estilos inline (guarda F8): los tamaños van por atributos width/height del SVG/img.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  useFrontlineAvatars,
  useAvatarUrl,
  useUpdateAvatar,
  getRecognitionErrorMessage,
} from '@/api/recognition';
import { useUserContext } from '@/contexts/UserContext';
import { getInitials } from '@/utils/strings';

/**
 * Placeholder neutro dibujado a mano (SVG, sin librerías ni estilos inline). Muestra las iniciales
 * si hay nombre; si no, una carita neutra. Es el fallback de `UserAvatar` cuando el avatar es null o
 * su id no está en el catálogo, para no mostrar nunca una imagen rota.
 *
 * El color de fondo es fijo (`#8A87B8`): esto NO es el avatar-por-persona de la paleta corporativa,
 * es el ícono genérico de "sin avatar todavía" antes de que exista uno que identificar.
 */
export function AvatarPlaceholder({ name, size = 56 }: Readonly<{ name?: string | null; size?: number }>) {
  const nombreLimpio = (name ?? '').trim();
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" className="avatar-face">
      <circle cx="32" cy="32" r="30" fill="#8A87B8" />
      {nombreLimpio ? (
        <text x="32" y="41" textAnchor="middle" fontSize="24" fontWeight="700" fill="#fff">
          {getInitials(nombreLimpio)}
        </text>
      ) : (
        <>
          <circle cx="23" cy="27" r="3.4" fill="#fff" />
          <circle cx="41" cy="27" r="3.4" fill="#fff" />
          <path d="M21 39 Q32 49 43 39" stroke="#fff" strokeWidth="3.2" fill="none" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

/**
 * Avatar de un usuario a partir de su `avatarId` (el id del catálogo dinámico que guarda su perfil).
 * Resuelve id→url con el catálogo cacheado (`useAvatarUrl`); si el id es null o no está en el
 * catálogo cae al placeholder de iniciales/carita neutra. Reutilizable donde se muestre el avatar de
 * un usuario (picker/perfil/sidebar/feed) sin romper lo existente.
 */
export function UserAvatar({
  avatarId,
  name,
  size = 56,
  className,
}: Readonly<{ avatarId?: string | null; name?: string | null; size?: number; className?: string }>) {
  const url = useAvatarUrl(avatarId);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);

  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        className={`avatar-img${className ? ` ${className}` : ''}`}
        onError={() => setFailed(true)}
      />
    );
  }
  return <AvatarPlaceholder name={name} size={size} />;
}

/**
 * Galería PRESENTACIONAL de avatares: estados (cargando/vacío/error), rejilla y marca del elegido.
 * **No persiste nada**: sólo avisa del id con `onSelect`.
 *
 * 🔴 Existe separada del `AvatarPicker` por una razón concreta, no por gusto arquitectónico. El
 * `AvatarPicker` guarda por `PATCH v1/users/{id}/avatar`, que exige que el correo del token coincida
 * con el del usuario destino — o sea, **sesión**. En el alta (`/app/registro`) no hay sesión ni id real
 * y el PATCH daría 401/403/404: es exactamente el motivo por el que el selector se quitó de esa
 * pantalla en su día. Ahí el avatar viaja en el CUERPO del alta, así que lo único que hace falta es
 * esta galería.
 *
 * Quien la monta decide qué hacer con «no hay catálogo» leyendo `useFrontlineAvatars()` por su cuenta
 * (es la misma `queryKey`, así que no hay una segunda petición): en el alta, eso desactiva la
 * obligatoriedad de elegir, porque una caída del blob storage no puede impedir registrarse a nadie.
 */
export function AvatarGallery({
  selectedId,
  onSelect,
  size = 56,
}: Readonly<{
  selectedId: string | null;
  onSelect: (avatarId: string) => void;
  size?: number;
}>) {
  const { t } = useTranslation('recognition');
  const avatars = useFrontlineAvatars();
  const catalog = avatars.data ?? [];

  return (
    <>
      {avatars.isLoading && (
        <p className="avatar-state" role="status">{t('avatar.loading')}</p>
      )}
      {avatars.isError && (
        <p className="rp-error" role="alert">
          {getRecognitionErrorMessage(avatars.error, t('avatar.loadError'))}
        </p>
      )}
      {!avatars.isLoading && !avatars.isError && catalog.length === 0 && (
        <p className="avatar-state avatar-empty" role="status">{t('avatar.empty')}</p>
      )}

      {catalog.length > 0 && (
        <div className="avatar-grid" role="radiogroup" aria-label={t('avatar.title')}>
          {catalog.map((a) => {
            const on = selectedId === a.id;
            return (
              <button
                key={a.id}
                type="button"
                role="radio"
                aria-checked={on}
                className={`avatar-opt ${on ? 'avatar-opt--on' : ''}`}
                onClick={() => onSelect(a.id)}
                aria-label={a.id}
              >
                <img src={a.url} alt="" width={size} height={size} className="avatar-img" />
                {on && <span className="avatar-check"><i className="fas fa-check" /></span>}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * RF-008 — el selector de avatar EN MODAL, que es como lo tenía la maqueta.
 *
 * 🔴 Por qué modal y no una rejilla dentro del formulario: la maqueta lo tenía así
 * (`avatar-picker-overlay` + `avatar-picker-modal` en el escenario de paridad visual del commit
 * `59eeff13`, con overlay a pantalla completa, desenfoque y una tarjeta de 400 px). Aquellas clases
 * murieron en el rework del módulo; el diseño se empata con el patrón de modal que esta misma pantalla
 * ya usa para el aviso legal, para que no haya dos modales distintos en el mismo formulario.
 *
 * Y aparte de empatar la maqueta, resuelve un problema real: con muchos avatares la rejilla en línea
 * estira el formulario y se va del scroll justo cuando hay que elegir.
 *
 * 🔴 NO SE PUEDE CERRAR SIN ELEGIR — no hay «×» ni se cierra al pinchar el fondo. Elegir es
 * obligatorio, así que una salida sería una forma de saltarse la regla.
 *
 * **La única excepción es el catálogo vacío o con error.** Ahí sí hay salida, porque si no una caída
 * del blob storage impediría registrarse a cualquiera: el modal dice qué pasó y ofrece continuar sin
 * avatar. Convertir un fallo de infraestructura en «nadie se puede dar de alta» es un daño mucho
 * mayor que un avatar sin elegir.
 */
export function AvatarPickerModal({
  avatarIdActual,
  onConfirmar,
  onContinuarSinAvatar,
}: Readonly<{
  /** El ya elegido, para que reabrir el modal no pierda la selección. */
  avatarIdActual: string | null;
  onConfirmar: (avatarId: string) => void;
  /** Sólo se ofrece cuando no hay nada que elegir (catálogo vacío o con error). */
  onContinuarSinAvatar: () => void;
}>) {
  const { t } = useTranslation('recognition');
  const avatars = useFrontlineAvatars();
  const [seleccion, setSeleccion] = useState<string | null>(avatarIdActual);

  // «No hay nada que ofrecer» sólo cuando YA SE SABE: la llamada falló, o terminó y vino vacía.
  // Mientras carga no es ninguna de las dos, y abrir la puerta ahí regalaría el avatar a quien
  // tenga prisa.
  const sinCatalogo = avatars.isError || (!avatars.isLoading && (avatars.data?.length ?? 0) === 0);

  // `createPortal` a `document.body`, y no es opcional: `.acc-card` deja un `transform` al acabar su
  // animación de entrada, y por especificación CSS eso lo convierte en el containing block de
  // cualquier `position: fixed` descendiente. Sin portal el overlay se queda encajado en la tarjeta
  // y `max-width` no sirve de nada. Mismo motivo que en `RegisterPolicyModal`.
  return createPortal((
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={t('avatar.title')}>
      {/* Sin `onClick` que cierre, ni en el fondo ni con una «×»: la única salida es elegir. */}
      <div className="policy-modal policy-modal--avatar">
        <div className="policy-modal-header">
          <span className="policy-modal-title">{t('avatar.title')}</span>
        </div>

        <div className="policy-modal-body">
          <p className="avatar-sub">{t('avatar.subtitle')}</p>
          <AvatarGallery selectedId={seleccion} onSelect={setSeleccion} />
        </div>

        <div className="policy-modal-footer">
          {/* 🔴 `.policy-accept-btn` de base ES el aspecto DESHABILITADO (`cursor: not-allowed`, fondo
              gris, texto apagado); lo habilitado lo da el modificador `--ready`. Sin él el botón
              funcionaba pero se veía bloqueado — reportado en QA el 2026-08-23. El `disabled` y la
              clase salen de la MISMA condición, para que no puedan volver a divergir. */}
          {sinCatalogo ? (
            <button
              type="button"
              className="policy-accept-btn policy-accept-btn--ready"
              onClick={onContinuarSinAvatar}
            >
              {t('avatar.continueWithout', 'Continuar sin avatar')}
            </button>
          ) : (
            <button
              type="button"
              className={`policy-accept-btn${seleccion ? ' policy-accept-btn--ready' : ''}`}
              disabled={!seleccion}
              onClick={() => seleccion && onConfirmar(seleccion)}
            >
              {t('avatar.confirm')}
            </button>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

interface Props {
  /** Callback opcional al confirmar (p.ej. avanzar el onboarding). Devuelve el ID del avatar. */
  onDone?: (avatarId: string) => void;
  /** Avatar preseleccionado (id del catálogo) — normalmente el que ya tenga el usuario. */
  initialAvatarId?: string;
}

/** Selector de avatar dinámico con persistencia real (RF-008). */
export function AvatarPicker({ onDone, initialAvatarId }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const { demoUser } = useUserContext();
  const avatars = useFrontlineAvatars();
  const updateAvatar = useUpdateAvatar();
  const [selected, setSelected] = useState<string | null>(initialAvatarId ?? null);
  const [saved, setSaved] = useState(false);

  const catalog = avatars.data ?? [];
  // Sin selección explícita, previsualiza/confirma el primero del catálogo (si hay).
  const previewId = selected ?? catalog[0]?.id ?? null;

  const confirm = () => {
    if (!previewId) return;
    updateAvatar.mutate(
      { id: demoUser.userId, body: { avatar: previewId } },
      { onSuccess: () => { setSaved(true); onDone?.(previewId); } },
    );
  };

  return (
    <div className="avatar-picker">
      <div className="avatar-preview">
        <UserAvatar avatarId={previewId} name={demoUser.nombre} size={96} />
      </div>
      <h2 className="avatar-title">{t('avatar.title')}</h2>
      <p className="avatar-sub">{t('avatar.subtitle')}</p>

      {/* La galería (estados + rejilla + marca) es la MISMA que monta el alta: lo único propio de
          este picker es la persistencia por PATCH, que en el alta no se puede usar (no hay sesión). */}
      <AvatarGallery
        selectedId={previewId}
        onSelect={(id) => { setSelected(id); setSaved(false); }}
      />

      {updateAvatar.isError && (
        <p className="rp-error" role="alert">{getRecognitionErrorMessage(updateAvatar.error, t('avatar.saveError'))}</p>
      )}
      {saved ? (
        <p className="rec-action-ok"><i className="fas fa-check" /> {t('avatar.saved')}</p>
      ) : (
        <button
          type="button"
          className="btn btn--send"
          disabled={updateAvatar.isPending || !previewId}
          onClick={confirm}
        >
          {updateAvatar.isPending ? t('admin.saving') : t('avatar.confirm')}
        </button>
      )}
    </div>
  );
}

export default AvatarPicker;
